import { onDown, onMove, onUp, onDbl } from './pointer.js'
import {
  autoGrowTextEditor,
  blurTextEditor,
  commitTextEdit,
} from './text-editor.js'
import { undo, redo } from './history.js'
import { setMode, selectLayer, applyModeCursor } from './mode.js'
import { fitStage, render, syncCanvasSize } from './rendering.js'
import { normalizeSource, doLoadImage } from './save-export.js'
import { deleteLayer } from './layer-actions.js'
import {
  panPointerDown,
  panPointerMove,
  panPointerUp,
  resetPan,
} from './touch-pan.js'

export function mount(state, self, refs) {
  state.canvasEl = refs.canvas
  state.overlayEl = refs.overlay
  state.textEditorEl = refs.textEditor
  state.stageEl = refs.stage
  state.ctx = state.canvasEl.getContext('2d')
  state.octx = state.overlayEl.getContext('2d')
  state.mounted = true
  applyModeCursor(state)

  state.overlayEl.addEventListener('lostpointercapture', (e) =>
    panPointerUp(state, e)
  )
  state.overlayEl.addEventListener('pointerdown', (e) => {
    e.preventDefault()
    if (e.isPrimary && state.panPointers?.size) resetPan(state)
    if (panPointerDown(state, e)) return
    onDown(state, e)
  })
  state.overlayEl.addEventListener('pointermove', (e) => {
    e.preventDefault()
    if (panPointerMove(state, e)) return
    onMove(state, e)
  })
  state.overlayEl.addEventListener('pointerup', (e) => {
    panPointerUp(state, e)
    onUp(state, e)
  })
  state.overlayEl.addEventListener('pointercancel', (e) => {
    resetPan(state)
    onUp(state, e)
  })
  state.overlayEl.addEventListener('dblclick', (e) => onDbl(state, e))

  // Commit, unfocus, deselect — guarded so an ordinary drag never deselects.
  const endTextEdit = () => {
    if (!state.editingLayer) return
    blurTextEditor(state)
    selectLayer(state, null)
  }

  state.textEditorEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      // Enter inserts a newline; stopped so no parent form reads it as submit.
      e.stopPropagation()
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault()
        endTextEdit()
      }
    } else if (e.key === 'Escape') {
      // Escape discards the edit, so restore nothing — just let go.
      e.stopPropagation()
      if (state.editingLayer) state.editingLayer._editing = false
      state.editingLayer = null
      state.textEditorEl.blur()
      selectLayer(state, null)
    }
  })
  state.textEditorEl.addEventListener('input', () => autoGrowTextEditor(state))
  state.textEditorEl.addEventListener('blur', () => commitTextEdit(state))

  // Capture phase: the overlay preventDefaults away the implicit blur.
  state.stageEl.addEventListener(
    'pointerdown',
    (e) => {
      if (e.target === state.textEditorEl) return
      endTextEdit()
      // Presses on the padding deselect; presses on the image are onDown's.
      if (e.target !== state.overlayEl && state.selectedId != null) {
        selectLayer(state, null)
      }
    },
    true
  )

  ;['dragover', 'dragenter'].forEach((ev) =>
    state.stageEl.addEventListener(ev, (e) => e.preventDefault())
  )
  state.stageEl.addEventListener('drop', (e) => {
    e.preventDefault()
    const f = e.dataTransfer.files[0]
    if (f && f.type.startsWith('image/'))
      normalizeSource(state, f, (d) => doLoadImage(state, d))
  })

  self._keyHandler = (e) => {
    if (!self.isOpen()) return
    const tag = document.activeElement && document.activeElement.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
    if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault()
      undo(state)
    } else if (
      (e.metaKey || e.ctrlKey) &&
      (e.key === 'y' || (e.shiftKey && e.key === 'z'))
    ) {
      e.preventDefault()
      redo(state)
    } else if (e.key === 'Escape') {
      if (state.mode !== 'select' || state.selectedId != null) {
        setMode(state, 'select')
        selectLayer(state, null)
      } else if (state.config.closeOnEscape !== false) self.close()
    } else if (
      (e.key === 'Delete' || e.key === 'Backspace') &&
      state.selectedId != null &&
      state.mode === 'select'
    ) {
      e.preventDefault()
      deleteLayer(state, state.selectedId)
    }
  }
  document.addEventListener('keydown', self._keyHandler)

  self._resizeHandler = () => {
    if (state.sourceCanvas.width && self.isOpen()) {
      fitStage(state)
      render(state)
    }
  }
  window.addEventListener('resize', self._resizeHandler)

  if (state.sourceCanvas.width) {
    syncCanvasSize(state)
    render(state)
  }
}
